// Board cache — instant render on refresh, revalidate in the background.
//
// Stores each board's loaded items/groups/columns in localStorage so reopening
// or refreshing a board shows it immediately instead of re-syncing from zero
// (like Monday's cache-first behaviour). The server's `no-store` header only
// blocks the HTTP cache — app-level storage like this is unaffected.
//
// Capped + TTL'd so it stays small; the board is always revalidated against the
// server right after the instant render, so stale data self-heals within a tick.

const CACHE_PREFIX = 'acuity_boardcache_v1_';
const CACHE_MAX_ITEMS = 300;                 // cap cached rows to stay within quota
const CACHE_TTL_MS = 1000 * 60 * 60 * 24;    // 24h

export function readBoardCache(boardId) {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + boardId);
    if (!raw) return null;
    const c = JSON.parse(raw);
    if (!c || (Date.now() - (c.ts || 0)) > CACHE_TTL_MS) return null;
    return c;
  } catch {
    return null;
  }
}

export function writeBoardCache(boardId, board, groups, items, hasMore) {
  try {
    const capped = (items || []).slice(0, CACHE_MAX_ITEMS);
    // Rebuild per-group offsets from the capped list so restored pagination
    // stays consistent with exactly what we cached.
    const offsets = {};
    capped.forEach((it) => { offsets[it.group_id] = (offsets[it.group_id] || 0) + 1; });
    const payload = JSON.stringify({
      board,
      groups,
      items: capped,
      offsets,
      // If we dropped rows to fit the cap, there is definitely more to load.
      hasMore: !!hasMore || (items || []).length > CACHE_MAX_ITEMS,
      ts: Date.now(),
    });
    localStorage.setItem(CACHE_PREFIX + boardId, payload);
  } catch {
    // Quota exceeded / serialization issue → drop this board's cache silently.
    try { localStorage.removeItem(CACHE_PREFIX + boardId); } catch (_) {}
  }
}

// Forget ONE board — when the server says this user can no longer open it, its
// cached rows must not keep rendering.
export function dropBoardCache(boardId) {
  try { localStorage.removeItem(CACHE_PREFIX + boardId); } catch { /* storage blocked */ }
}

// Clear every board cache — call on logout so data doesn't leak to the next user
// on a shared machine.
export function clearBoardCaches() {
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith(CACHE_PREFIX)
        // Workspace-scoped SWR caches (folders, collaborators, boards lists) —
        // same shared-machine rule: never leak to the next user.
        || k.startsWith('folders_cache_')
        || k.startsWith('ws_collab_cache_')
        || k.startsWith('boards_cache_')
        || k === 'workspaces_cache')
      .forEach((k) => localStorage.removeItem(k));
  } catch (_) {}
}
